import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useNavigate } from '@common/hooks';
import { Button, message } from 'antd';

import { createMessage } from 'works/legacy/lib/message-helper';
import { CreateWorksSubmittalPacket } from 'works/graphql/works-submittal-packet';
import {
  CreateStaffingRequestApplicationMutation,
  CreateWorksProfileIdentificationCode,
} from '@applicant-flow/graphql/mutations';
import { StaffingRequestSubmissionDrawer } from '@jobs/pages';
import { PrimaryButton } from '@common/components';
import { LastFourSsnModal } from './last-four-ssn-modal';
import { DoNotRehireDrawer } from './do-not-rehire-drawer';
import { DoNotRehireCommentDrawer } from './do-not-rehire-comment-drawer';

export function StaffingRequestSubmissionButton({
  staffingRequest,
  selectedWorksProfile,
  primary,
  doNotRehire,
}) {
  const [submissionFormVisible, setSubmissionFormVisible] = useState(false);
  const [ssnFormVisible, setSsnFormVisible] = useState(false);
  const [doNotRehireDrawerVisible, setDoNotRehireDrawerVisible] =
    useState(false);
  const [doNotRehireDrawerCommentVisible, setDoNotRehireDrawerCommentVisible] =
    useState(false);
  const [doNotRehireComment, setDoNotRehireComment] = useState(null);
  const [isProfileDoNotRehire, setIsProfileDoNotRehire] = useState(doNotRehire);

  const navigate = useNavigate();

  const createStaffingRequestApplicationAction = function () {
    return {
      onCompleted: (data) => {
        if (
          data.createStaffingRequestApplication.errors &&
          data.createStaffingRequestApplication.errors.length > 0
        ) {
          createMessage(
            'error',
            `Failed to submit request application. ${data.createStaffingRequestApplication.errors[0].fullErrors[0]}.`
          );
        } else {
          const worksProfile =
            data.createStaffingRequestApplication.staffingRequestApplication
              .worksProfile;
          const candidateName = worksProfile.fullName;
          createMessage('success', `${candidateName} has been submitted`);
          navigate(
            `/works/supplier/staffing-request/${staffingRequest.id}/submitted`
          );
        }
      },
      refetchQueries: ['SupplierJobPageQuery'],
      onError: () => {
        createMessage(
          'error',
          'Failed to submit request application. Please try again or contact your hiring partner.'
        );
      },
    };
  };

  const [createStaffingRequestApplication] = useMutation(
    CreateStaffingRequestApplicationMutation,
    createStaffingRequestApplicationAction()
  );

  const [createWorksProfileIdentificationCode, { loading: ssnSubmitLoading }] =
    useMutation(CreateWorksProfileIdentificationCode, {
      onError: () => {
        message.error('Error saving last four SSN');
      },
      onCompleted: (data) => {
        if (data.createWorksProfileIdentificationCode?.worksProfile) {
          message.success('Last four SSN has been saved');
          setSsnFormVisible(false);
          if (data.createWorksProfileIdentificationCode.doNotRehire) {
            setIsProfileDoNotRehire(true);
            setDoNotRehireDrawerVisible(true);
          } else {
            setSubmissionFormVisible(true);
          }
        }
      },
    });

  const [createWorkSubmittalPacket] = useMutation(CreateWorksSubmittalPacket, {
    onError(error) {
      console.error('createWorkSubmittalPacket error:', error);
    },
  });

  const getSubmittalPacketId = async ({ submittalPacket }) => {
    const submittalPacketId = submittalPacket?.target?.value;

    if (submittalPacketId) return submittalPacketId;

    const { data } = await createWorkSubmittalPacket({
      variables: {
        worksProfileId: selectedWorksProfile.worksProfileId,
        file: submittalPacket.file,
      },
    });

    return data?.createWorksSubmittalPacket?.worksSubmittalPacket?.id;
  };

  const handleSubmit = async (values) => {
    const submittalPacketId = await getSubmittalPacketId(values);

    createStaffingRequestApplication({
      variables: {
        staffingRequestId: staffingRequest.id,
        worksProfileId: selectedWorksProfile.worksProfileId,
        worksSubmittalPacketId: submittalPacketId,
        input: {
          interviewAvailability: values.interviewAvailability,
          availableToStartAt: values.availableToStartAt,
          timeoffDescription: values.timeoffDescription,
          doNotRehireComment: doNotRehireComment,
          employmentType: values.employmentType,
        },
      },
    });
  };

  const handleSsnSubmit = (values) => {
    createWorksProfileIdentificationCode({
      variables: {
        worksProfileId: selectedWorksProfile.worksProfileId,
        lastFourSsn: values.lastFourSsn,
        staffingRequestId: staffingRequest.id,
      },
    });
  };

  const handleDoNotRehireSubmit = () => {
    setDoNotRehireDrawerVisible(false);
    setDoNotRehireDrawerCommentVisible(true);
  };

  const handleDoNotRehireCommentSubmit = (comment) => {
    setDoNotRehireComment(comment);
    setDoNotRehireDrawerCommentVisible(false);
    setSubmissionFormVisible(true);
  };

  const ButtonComponent = primary ? PrimaryButton : Button;

  return (
    <>
      <LastFourSsnModal
        visible={ssnFormVisible}
        handleSubmit={handleSsnSubmit}
        onClose={() => setSsnFormVisible(false)}
        profileName={selectedWorksProfile.fullName}
        submitLoading={ssnSubmitLoading}
      />

      {isProfileDoNotRehire && (
        <DoNotRehireDrawer
          visible={doNotRehireDrawerVisible}
          handleSubmit={handleDoNotRehireSubmit}
          onClose={() => setDoNotRehireDrawerVisible(false)}
          profile={selectedWorksProfile}
          supplierId={selectedWorksProfile.supplierId}
          staffingRequest={staffingRequest}
        />
      )}

      {isProfileDoNotRehire && (
        <DoNotRehireCommentDrawer
          visible={doNotRehireDrawerCommentVisible}
          handleSubmit={handleDoNotRehireCommentSubmit}
          onClose={() => {
            setDoNotRehireDrawerCommentVisible(false);
            setDoNotRehireDrawerVisible(true);
          }}
        />
      )}

      <StaffingRequestSubmissionDrawer
        onSubmit={handleSubmit}
        visible={submissionFormVisible}
        onClose={() => {
          setSubmissionFormVisible(false);
          if (isProfileDoNotRehire) {
            setDoNotRehireDrawerCommentVisible(true);
          }
        }}
        selectedWorksProfile={selectedWorksProfile}
        staffingRequestType={staffingRequest.staffingRequestType}
        doNotRehireComment={doNotRehireComment}
      />

      <ButtonComponent
        disabled={
          !staffingRequest.canSupplierSubmitApplications.value ||
          staffingRequest.submissionsPaused
        }
        onClick={() => {
          if (
            selectedWorksProfile.profileType === 'domestic' &&
            !selectedWorksProfile.identificationCode
          ) {
            setSsnFormVisible(true);
          } else if (isProfileDoNotRehire) {
            setDoNotRehireDrawerVisible(true);
          } else {
            setSubmissionFormVisible(true);
          }
        }}
      >
        Start Submission
      </ButtonComponent>
    </>
  );
}

StaffingRequestSubmissionButton.propTypes = {
  staffingRequest: PropTypes.object.isRequired,
  selectedWorksProfile: PropTypes.shape({
    worksProfileId: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
    worksSubmittalPackets: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentStaffingRequestAssignments: PropTypes.arrayOf(PropTypes.object)
      .isRequired,
    profileType: PropTypes.string.isRequired,
    identificationCode: PropTypes.string,
    supplierId: PropTypes.string.isRequired,
  }),
  primary: PropTypes.bool,
  doNotRehire: PropTypes.bool,
};

StaffingRequestSubmissionButton.defaultProps = {
  primary: true,
};
